import includes from 'lodash/includes';
import find from 'lodash/find';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import range from 'lodash/range';
import BUILTIN_FUNCTIONS from '../data/BUILTIN_FUNCTIONS';
import uniqueVariables from '../data/uniqueVariables';
import expressions from '../data/expressions';
import builtinArgs from '../data/BUILTIN_ARGS';
import VariableRole from '../data/VariableRole';
import list from '../data/variables';
const variablesData = list.variables;

const LogLevel = {
	NOTICE: 'NOTICE',
	WARNING: 'WARNING',
	ERROR: 'ERROR'
};

variablesData.filter(({ role }) => role === 'EXPRESSION').forEach(({ id }) => {
	expressions[id] = expressions[id] || {};
	expressions[id].f = expressions[id].f || '0';
	expressions[id]._f = new Function(
		'i',
		'f',
		'time',
		`return ${expressions[id].f}`
	);
});
const VOID_EXPRESSION = {
	inputs: [],
	_f: () => 0
};

function compute_expression(variableData, time, context) {
	const { expressions, params, values, logs, CIRCULARITY_CHECK } = context;

	if (includes(CIRCULARITY_CHECK, variableData.id)) {
		logs.log(
			`Fatal Error: circularity detected on variableData ${variableData.id}. ` +
			`Stack: [${CIRCULARITY_CHECK}]`,
			LogLevel.ERROR
		);
		return NaN;
	}

	if (!expressions[variableData.id]) {
		logs.log(
			`Cannot find expression for variableData ${variableData.id}`,
			LogLevel.WARNING
		);
	}

	const expression = expressions[variableData.id] || VOID_EXPRESSION;
	CIRCULARITY_CHECK.push(variableData.id);

	const args = { i: {}, f: BUILTIN_FUNCTIONS };
	for (let input in expression.inputs) {
		const { variable: variableID, timeshift } = expression.inputs[input];
		const inputVar = find(variablesData, { id: variableID });
		if (!inputVar) {
			logs.log(
				`Fatal Error: input variable "${input}" is not defined for variable "${
				variableData.id
				}".`
			);
			return NaN;
		} else {
			args.i[input] = timeshift
				? (
					find(values, {
						time: time - timeshift,
						variable: variableID
					}) || { value: 0 }
				).value
				: compute_variable(inputVar, time, context, CIRCULARITY_CHECK).value;
		}
	}
	args.i.builtinArgs = builtinArgs.reduce((acc, arg) => {
		const builtinVar = find(params, { id: `${arg.id}:${time}` });
		acc[arg.id] = builtinVar && builtinVar.value ? builtinVar.value : 0;
		return acc;
	}, {});

	if (!isFunction(expression._f)) {
		throw new Error(
			`Expression "${variableData.id}" has no function connected.`
		);
	}

	const res = expression._f(args.i, args.f, time);
	if (!isNumber(res)) {
		throw new Error(`Expression "${variableData.id}" is NaN on ${time}"`);
	}

	return res;
}

function compute_variable(variableData, time, context, CIRCULARITY_CHECK = []) {
	const { params, decisions, VARIABLES_CACHE, logs } = context;
	const year = includes(uniqueVariables, variableData.id) ? 0 : time;
	if (
		variableData.role === VariableRole.DECISION ||
		variableData.role === VariableRole.PARAM
	) {
		const pool =
			variableData.role === VariableRole.DECISION ? decisions : params;
		VARIABLES_CACHE[variableData.id] = find(pool, {
			id: `${variableData.id}:${year}`
		});
		if (
			!VARIABLES_CACHE[variableData.id] &&
			VARIABLES_CACHE[variableData.id] !== 0
		) {
			logs.log(
				`Fatal Error: Variable ${variableData.id} have not valid value: ${
				VARIABLES_CACHE[variableData.id]
				}`
			);
		}
		return VARIABLES_CACHE[variableData.id];
	}
	if (variableData.role === VariableRole.EXPRESSION) {
		VARIABLES_CACHE[variableData.id] =
			VARIABLES_CACHE[variableData.id] !== undefined
				? VARIABLES_CACHE[variableData.id]
				: compute_expression(variableData, time, {
					...context,
					CIRCULARITY_CHECK
				});
		if (
			!VARIABLES_CACHE[variableData.id] &&
			VARIABLES_CACHE[variableData.id] !== 0
		) {
			logs.log(
				`Fatal Error: VARIABLES_CACHE of variableData ${
				variableData.id
				} have not valid value: ${VARIABLES_CACHE[variableData.id]}`
			);
		}
		return { value: VARIABLES_CACHE[variableData.id] };
	}
	throw new Error(`Cannot handle variableData of role ${variableData.role}.`);
}

function compute_time(context, time) {
	const VARIABLES_CACHE = {};
	const updates = [];
	for (let variableData of variablesData) {
		const year = includes(uniqueVariables, variableData.id) ? 0 : time;
		updates.push({
			id: `${variableData.id}:${year}`,
			year,
			name: variableData.id,
			disabled: false,
			variable: variableData.id,
			time: year,
			value: compute_variable(variableData, year, {
				...context,
				VARIABLES_CACHE
			}).value
		});
	}
	return updates;
}

function generate_context(variables) {
	const decisions = [];
	const params = [];
	variablesData.forEach(variableData => {
		if (
			variableData.role === VariableRole.DECISION ||
			variableData.role === VariableRole.PARAM
		) {
			(includes(uniqueVariables, variableData.id) ? [0] : range(1, 4)).map(
				i => {
					const variable = find(variables, {
						name: variableData.id,
						year: i
					});
					if (!variable) {
						console.log(
							`Fatal Error: can't find variable with id ${
							variableData.id
							} and year ${i}`
						);
					}
					const item = {
						id: `${variableData.id}:${i}`,
						variable: variableData.id,
						time: i,
						value: variable.value
					};
					if (variableData.role === VariableRole.DECISION) {
						decisions.push(item);
					}
					if (variableData.role === VariableRole.PARAM) {
						params.push(item);
					}
				}
			);
		}
	});

	const context = {
		decisions,
		params,
		expressions,
		logs: console,
		values: []
	};

	return context;
}

export default function compute(variables) {
	const context = generate_context(variables);
	let updates = [];
	let updatesArray = [];
	for (let time = 1; time < 4; time++) {
		context.values = [...updates, ...context.values];
		updates = compute_time(context, time);
		updatesArray = [...updatesArray, ...updates];
		// for (let update of updates) {
		// 	if (!update) {
		// 		console.log(`Fatal Error: input variable ${update} is not defined.`);
		// 	}
		// 	await db.setComputeValue(
		// 		businessPlan.getVariableKey(update.variable, update.time),
		// 		update.value
		// 	);
		// }
	}

	return updatesArray.map(ele => ({
		disabled: false,
		key: ele.id,
		name: ele.name,
		showInGui: true,
		value: ele.value,
		year: ele.year
	}));
}
